// 05-11 (#276, VIV-1 y VIV-7) — el principio del turno en curso de los que trabajan, sin abrir su chat.
//
// El paso en vivo («Paso 7 · …», paso-en-vivo.ts) se cuenta desde el principio del turno, y la web solo lo tenía si lo
// había visto llegar por el socket: al abrir la página a mitad de un turno largo (VIV-1) la fila decía qué hacía SIN
// número; tras un corte del socket o un reinicio del servidor (VIV-7) la cuenta se quedaba corta (lo de durante el corte
// no llegó). Ahora, al abrir la oficina y al volver a conectar, se pide a la ruta de mensajes de siempre
// (`GET /api/agents/:id/messages`, la misma con la que el chat pagina hacia atrás) desde ahora hacia atrás, hasta que
// aparece el borde del turno (`conoceElTurno`), y se junta con lo que ya había (`mergeMessages`). La fuente sigue siendo
// UNA: `messagesByAgent`. Nada nuevo en el servidor (ni ruta, ni tabla, ni columna).
//
// Si no se encuentra el borde en `MAX_PAGINAS` (un turno o un corte de más de 2000 mensajes) o la petición falla, la fila sigue sin
// número, como antes: nunca se inventa. El próximo reconectar lo vuelve a intentar.
//
// Coste acotado (05-11, revisión de la integración): se para en cuanto lo que YA había más lo traído conoce el borde del
// turno —tras un corte, la primera página (lo más nuevo) suele bastar: el principio ya estaba—; como mucho
// `EN_PARALELO` agentes a la vez; y volver a la app sin que el socket se haya cortado no pide nada (lo vivido llegó).
import { fetchAgentMessages } from '../../../lib/api';
import { useAppStore } from '../../../stores/app.store';
import type { Message } from '../../../types';
import { useOficinaStore } from '../store';
import { conoceElTurno } from './paso-en-vivo';

/** Mensajes por petición: los mismos que la paginación del chat (loadOlderMessages). */
export const PAGINA = 200;
/** Como mucho, tantas páginas hacia atrás (= el tope de `ensureStickyAnchorLoaded` del chat). */
export const MAX_PAGINAS = 10;
/** Agentes que se ponen al día a la vez (abrir la oficina con 15 trabajando no lanza 15 peticiones de golpe). */
export const EN_PARALELO = 3;

const enVuelo = new Map<string, Promise<void>>();

/** Trae el turno en curso de un agente (desde ahora hacia atrás hasta su borde) y lo junta con lo que hay. Uno a la vez por agente. */
export function traerTurnoEnCurso(agentId: string): Promise<void> {
  const ya = enVuelo.get(agentId);
  if (ya) return ya;
  const p = (async () => {
    // Lo que la web ya tenía ANTES de pedir: solo se para de paginar cuando lo traído llega hasta ahí (sin hueco en medio)
    // y, con eso, se conoce el principio del turno. Parar antes dejaba un hueco y un «Paso N» bajo que parecía exacto
    // (revisión 05-11: un agente escribió más de 200 mensajes mientras el PC dormía).
    const yaHabia = new Set((useAppStore.getState().messagesByAgent[agentId] ?? []).map((x) => x.id));
    const traidos: Message[] = [];
    let completo = false;
    let antes: { beforeTs: string; beforeId: string } | undefined;
    for (let i = 0; i < MAX_PAGINAS; i += 1) {
      const r = await fetchAgentMessages(agentId, { ...antes, limit: PAGINA });
      const pagina = r.messages as Message[];
      traidos.unshift(...pagina);
      const masViejo = pagina[0];
      if (!masViejo || !r.hasMore || conoceElTurno(traidos)) {
        // Llegó al principio de la conversación o el borde del turno está en lo traído: el turno entero está aquí.
        completo = true;
        break;
      }
      // Lo traído ya enlaza con lo que había (el más viejo de la página ya estaba): sin hueco; si lo de antes conocía el
      // borde, el turno queda completo.
      if (yaHabia.has(masViejo.id) && conoceElTurno([...(useAppStore.getState().messagesByAgent[agentId] ?? []), ...traidos])) {
        completo = true;
        break;
      }
      antes = { beforeTs: masViejo.timestamp, beforeId: masViejo.id };
    }
    // Si se rindió (MAX_PAGINAS) sin llegar a lo que había, quedaría un hueco en medio y la cuenta saldría baja como si
    // fuera exacta: se olvida lo de antes del hueco (en la memoria de la lista; su chat lo recarga entero al abrirse) y la
    // fila dice qué hace SIN número. Nunca se inventa.
    if (!completo && traidos[0]) olvidarAntesDe(agentId, traidos[0]);
    useAppStore.getState().mergeMessages(agentId, traidos);
  })()
    // Error de red o agente que ya no está (404): la fila se queda sin número (lo honesto) y el próximo reconectar
    // reintenta. Nada que avisar a Pau: no ha pedido nada.
    .catch(() => undefined)
    .finally(() => enVuelo.delete(agentId));
  enVuelo.set(agentId, p);
  return p;
}

/**
 * Pone al día el turno en curso de los que trabajan (según la oficina recién pedida). `forzar` (al volver a conectar):
 * todos, porque durante el corte pudo perderse algo en medio; si no (al abrir la oficina), solo a quien le falta el
 * borde del turno. El del chat abierto se salta: su chat ya recarga la conversación entera (`recargarChatAbierto`).
 */
export function ponerAlDiaLosTurnos({ forzar }: { forzar: boolean }): Promise<void> {
  const { contratados, chatAbierto } = useOficinaStore.getState();
  const mensajes = useAppStore.getState().messagesByAgent;
  const pedir = Object.values(contratados)
    .filter((c) => c.situacion === 'trabajando' && c.agentId !== chatAbierto)
    .filter((c) => forzar || !conoceElTurno(mensajes[c.agentId] ?? []))
    .map((c) => c.agentId);
  return deAPocos(pedir, EN_PARALELO, traerTurnoEnCurso);
}

/** Ejecuta `tarea` para cada id con como mucho `n` a la vez. */
async function deAPocos(ids: readonly string[], n: number, tarea: (id: string) => Promise<void>): Promise<void> {
  const cola = [...ids];
  const trabajador = async () => {
    for (let id = cola.shift(); id !== undefined; id = cola.shift()) await tarea(id);
  };
  await Promise.all(Array.from({ length: Math.min(n, cola.length) }, trabajador));
}

/** Quita de la memoria de un agente lo anterior a `desde` (lo que queda detrás de un hueco que no se pudo rellenar). */
function olvidarAntesDe(agentId: string, desde: Message): void {
  useAppStore.setState((st) => {
    const lista = st.messagesByAgent[agentId];
    if (!lista) return st;
    const quedan = lista.filter((x) => x.timestamp > desde.timestamp || (x.timestamp === desde.timestamp && x.id >= desde.id));
    return { messagesByAgent: { ...st.messagesByAgent, [agentId]: quedan } };
  });
}

